import type { Vec3 } from '../../sim/mathx';
import type { TrackState } from '../../sim/trackTypes';

export interface PpiConfig {
  mountEnu: Vec3;
  mountYaw: number;
  rangeMax: number;
}

export interface PpiTrack {
  id: number;
  state: TrackState;
  posEnu: Vec3;
}

export interface PpiScene {
  rings: number[];
  blips: Array<{ id: number; x: number; y: number; state: TrackState }>;
  sweep: { x0: number; y0: number; x1: number; y1: number };
  scout: { x: number; y: number };
}

/** Pure ENU-to-north-up PPI projection; all coordinates are normalized by rangeMax. */
export const computePpiScene = (
  config: PpiConfig,
  tracks: readonly PpiTrack[],
  scoutEnu: Vec3,
  sweepAzimuth: number,
): PpiScene => {
  if (!Number.isFinite(config.rangeMax) || config.rangeMax <= 0) {
    throw new RangeError('PPI rangeMax must be finite and > 0');
  }
  const invRange = 1 / config.rangeMax;
  const blips = tracks
    .map((track) => ({
      id: track.id,
      x: (track.posEnu.x - config.mountEnu.x) * invRange,
      y: (track.posEnu.y - config.mountEnu.y) * invRange,
      state: track.state,
    }))
    .filter((blip) => Math.hypot(blip.x, blip.y) <= 1 + Number.EPSILON);
  const worldSweepAzimuth = config.mountYaw + sweepAzimuth;
  return {
    rings: [0.25, 0.5, 0.75, 1],
    blips,
    sweep: {
      x0: 0,
      y0: 0,
      x1: Math.cos(worldSweepAzimuth),
      y1: Math.sin(worldSweepAzimuth),
    },
    scout: {
      x: (scoutEnu.x - config.mountEnu.x) * invRange,
      y: (scoutEnu.y - config.mountEnu.y) * invRange,
    },
  };
};

const BLIP_COLORS: Readonly<Record<TrackState, string>> = Object.freeze({
  tentative: '#76a7bd',
  confirmed: '#4dff9a',
  coasting: '#ffb547',
  dropped: '#ff6b6b',
});

/** Canvas adapter kept separate from the pure projection for headless use. */
export const drawPpi = (
  ctx: CanvasRenderingContext2D | undefined,
  scene: PpiScene,
  size: number,
): void => {
  if (!ctx || !Number.isFinite(size) || size <= 0) return;
  const center = size / 2;
  const radius = size * 0.46;
  const toCanvas = (x: number, y: number): [number, number] => [
    center + x * radius,
    center - y * radius,
  ];

  ctx.save();
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = 'rgba(2, 18, 18, 0.88)';
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(74, 255, 183, 0.28)';
  ctx.lineWidth = 1;
  for (const ring of scene.rings) {
    ctx.beginPath();
    ctx.arc(center, center, radius * ring, 0, Math.PI * 2);
    ctx.stroke();
  }

  const [sweepX0, sweepY0] = toCanvas(scene.sweep.x0, scene.sweep.y0);
  const [sweepX1, sweepY1] = toCanvas(scene.sweep.x1, scene.sweep.y1);
  ctx.strokeStyle = 'rgba(98, 230, 255, 0.8)';
  ctx.beginPath();
  ctx.moveTo(sweepX0, sweepY0);
  ctx.lineTo(sweepX1, sweepY1);
  ctx.stroke();

  for (const blip of scene.blips) {
    const [x, y] = toCanvas(blip.x, blip.y);
    ctx.fillStyle = BLIP_COLORS[blip.state];
    ctx.beginPath();
    ctx.arc(x, y, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  const [scoutX, scoutY] = toCanvas(scene.scout.x, scene.scout.y);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(scoutX - 2, scoutY - 2, 4, 4);
  ctx.restore();
};
